import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebouncedValue, useUpdateEffect } from '@private/hooks';

import NoItemsFound from 'components/NoItemsFound';
import { appReactMemo } from 'hocs';
import { useAppDispatch, useAppSelector } from 'hooks/storeHooks';
import {
  FILTER_OPERATIONS_SORT_STRATEGIES,
  setFilterOperationsSortStrategy,
} from 'store/userConfiguration';
import { IFilterSetting } from 'utils/types';

import {
  BodyWrapper,
  NotFoundWrapper,
  SearchFilterInput,
  StyledSwitchInput,
} from '../../styled';
import SectionItem from '../SectionItem';

interface IBody {
  filtersSectionsList: [string, Map<string, IFilterSetting>][];
}

const Body = ({ filtersSectionsList }: IBody) => {
  const { t } = useTranslation();

  const filterOperationsSortStrategy = useAppSelector(
    (state) => state.userConfiguration.filterOperationsSortStrategy,
  );

  const dispatch = useAppDispatch();

  const hasResults = React.useRef<boolean | null>(null);
  const [hasFiltrationResults, setHasFiltrationResults] = React.useState<boolean | null>(
    null,
  );
  const [filterSearch, setFilterSearch] = React.useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useDebouncedValue<string>(500, '');

  const onSearchFilter = React.useCallback((searchText: string) => {
    setFilterSearch(searchText);
  }, []);

  const onSwitchChange = React.useCallback(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      dispatch(
        setFilterOperationsSortStrategy({
          filterOperationsSortStrategy: checked
            ? FILTER_OPERATIONS_SORT_STRATEGIES.EQUALS_FIRST
            : FILTER_OPERATIONS_SORT_STRATEGIES.LIKES_FIRST,
        }),
      );
    },
    [dispatch],
  );

  useUpdateEffect(() => {
    setDebouncedSearch(filterSearch);
    if (!filterSearch) {
      setHasFiltrationResults(null);
      hasResults.current = null;
    }
  }, [filterSearch]);

  const renderBodyContent = () => {
    if (hasFiltrationResults === false && debouncedSearch) {
      return (
        <NotFoundWrapper>
          <NoItemsFound />
        </NotFoundWrapper>
      );
    }

    return filtersSectionsList.map(([key, value], index) => (
      <SectionItem
        filtrationResultsHandler={setHasFiltrationResults}
        hasFiltrationResults={hasResults}
        isInitialOpen={filtersSectionsList?.length === 1}
        isLast={index === filtersSectionsList.length - 1}
        key={key}
        searchTerm={debouncedSearch}
        sectionKey={key}
        sectionList={value}
      />
    ));
  };

  return (
    <BodyWrapper>
      <StyledSwitchInput
        checked={
          filterOperationsSortStrategy === FILTER_OPERATIONS_SORT_STRATEGIES.EQUALS_FIRST
        }
        hintText={t('common.filters.filterOperationsSortStrategy')}
        label={t('common.filters.equalsFirst')}
        onChange={onSwitchChange}
      />
      <SearchFilterInput
        label={t('common.filters.searchFilter')}
        onChange={onSearchFilter}
        value={filterSearch}
      />
      {renderBodyContent()}
    </BodyWrapper>
  );
};

export default appReactMemo(Body);
